import axios from 'axios'

import { RedactionAiError } from '@/common/document-viewer/types'

export default {
  getFile (mnemo, id, params = {}) {
    return axios.get(`room/${mnemo}/file/${id}`, { params })
  },
  getFileViewerInfo (mnemo, fileId, data, request) {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()
    request.cancelToken = source
    return axios.post(
      `room/${mnemo}/file/${fileId}/viewer-info`,
      data,
      { cancelToken: source.token },
    )
  },
  redactDocument (mnemo, fileId, data) {
    return axios.post(`room/${mnemo}/file/${fileId}/redact-pdf`, data)
  },
  async searchByAiStream (mnemo, fileId, { onEntity, onSummary, onError }) {
    // Axios to build the URL, params, etc.
    const url = axios.getUri({
      url: `room/${mnemo}/file/${fileId}/entities`,
      method: 'POST',
    })

    // Fetch to read a ReadableStream (axios doesn't support stream in browser)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(axios.defaults.headers.common || {}),
      },
      body: JSON.stringify({
        room_mnemo: mnemo,
        file_id: fileId,
      }),
      credentials: 'include',
    })

    // Service down (cannot start)
    if (!response.ok) {
      throw new RedactionAiError(
        'SERVICE_DOWN',
        `R&D service error (HTTP ${response.status})`,
        response.status,
      )
    }

    if (!response.body) {
      throw new RedactionAiError('STREAMING_NOT_SUPPORTED', 'Streaming not supported by browser')
    }

    await parseNDJSONStream(response.body, response.status, onEntity, onSummary, onError)
  },
}

export async function parseNDJSONStream (stream, httpStatus, onEntity, onSummary, onError) {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  async function processChunk ({ done, value }) {
    if (done) return
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() // Keep incomplete line in buffer

    for (const line of lines) {
      if (!line.trim()) continue

      let json
      try {
        json = JSON.parse(line)
      } catch (e) {
        const err = new RedactionAiError(
          'ERROR_AFTER_CHUNKS',
          `NDJSON parsing error: ${e?.message || String(e)}`,
          httpStatus,
        )
        onError(err)
        await reader.cancel()
        throw err
      }

      // Explicit error chunk from backend
      if (json.class === 'error') {
        const err = new RedactionAiError(
          json.code || 'ERROR_AFTER_CHUNKS',
          json.message || 'Streaming error',
          httpStatus,
        )
        onError(err)
        await reader.cancel()
        throw err
      }

      // Chunk detected entity
      if (json.class === 'entity') {
        onEntity({
          ...json,
          entity_id: String(json.entity_id),
        })
        continue
      }

      // Chunk final summary
      if (json.class === 'entity_norm_values') {
        // Normalize ids to values
        json.entities = (json.entities || []).map(e => ({
          type: e.type,
          norm: e.norm,
          values: e.values ?? e.ids ?? [],
        }))
        onSummary(json)
        continue
      }

      // Unknown chunks
      console.warn('[Redaction AI] Unknown chunk', json)
    }

    return reader.read().then(processChunk)
  }

  try {
    await reader.read().then(processChunk)

    // flush remaining buffer (last line without \n)
    if (buffer.trim()) {
      let json
      try {
        json = JSON.parse(buffer)
      } catch (e) {
        const err = new RedactionAiError(
          'ERROR_AFTER_CHUNKS',
          `NDJSON parsing error (flush): ${e?.message || String(e)}`,
          httpStatus,
        )
        onError(err)
        throw err
      }

      if (json.class === 'error') {
        const err = new RedactionAiError(
          json.code || 'ERROR_AFTER_CHUNKS',
          json.message || 'Streaming error',
          httpStatus,
        )
        onError(err)
        throw err
      }

      if (json.class === 'entity') onEntity({ ...json, entity_id: String(json.entity_id) })

      if (json.class === 'entity_norm_values') {
        json.entities = (json.entities || []).map(e => ({
          type: e.type,
          norm: e.norm,
          values: e.values ?? e.ids ?? [],
        }))
        onSummary(json)
      }
    }
  } catch (e) {
    if (!(e instanceof RedactionAiError)) {
      const err = new RedactionAiError(
        'ERROR_AFTER_CHUNKS',
        e?.message || String(e) || 'Stream failed',
        httpStatus,
      )
      onError(err)
      throw err
    }
    throw e
  } finally {
    try {
      reader.releaseLock()
    } catch (e) {
      console.debug('[Redaction AI] releaseLock failed', e)
    }
  }
}
